import { HttpClient } from '@angular/common/http';
import {
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import {
  NbDialogService,
  NbMenuItem,
  NbMenuService,
  NbPopoverDirective,
  NbSidebarService,
} from '@nebular/theme';
import { productFruits } from 'product-fruits';
import { triggerAnalyticsEvent } from 'src/app/utils/analytics/trigger-analytics';
import { ApplicationControllerService } from 'src/app/cc-api/services';
import { Subject, takeUntil, filter } from 'rxjs';
import mixpanel from 'mixpanel-browser';
import { SimpleOauth2User } from 'src/app/cc-api/models/simple-oauth-2user';
import {
  Router,
  NavigationEnd,
  NavigationStart,
  ActivatedRoute,
} from '@angular/router';
import { UserAuthoritiesService } from 'src/app/services/user-authorities.service';
import { UserService } from 'src/app/services/user.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { CookieUtilWrapperService } from 'src/app/services/cookie-util-wrapper.service';
import { SessionContextStoreService } from 'src/app/services/session-context-store.service';
import { RedirectOnLoginService } from 'src/app/services/redirect-on-login.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { TableStatefulFiltersService } from 'src/app/services/table-stateful-filters.service';
import { GenerateTicketURLService } from './generate-ticket-url.service';
import {
  GLOBAL_SEARCH_ICON_CLICK,
  RAISE_A_TICKET_CLICK,
} from 'src/app/constants/analytics-track-events';
import { SidebarV2Component } from 'src/app/components/sidebar-v2/sidebar.component';
import {
  helpCenterQuickLinks,
  myAccountQuickLinks,
} from 'src/app/constants/sidebar-quick-links';
import { BillingService } from 'src/app/services/billing/billing.service';
import { showPaasSaasSwitch } from 'src/app/constants/saas';
import { PendoService } from 'src/app/services/pendo.service';
import { IntentDetailsService } from 'src/app/services/intent-details.service';
import { ContextWidgetStorageService } from 'src/app/components/context-widget/services/context-storage.utils.service';

const PRODUCT_FRUITS_WORKSPACE_CODE = 'bHxPCwQd5YABffej';

@Component({
  selector: 'app-layout',
  templateUrl: './app-layout.component.html',
  styleUrls: ['./app-layout.component.scss'],
})
export class AppLayoutComponent implements OnInit, OnDestroy {
  @ViewChild(SidebarV2Component) sidebarComponent!: SidebarV2Component;
  @ViewChild('referenceMenuPopover', { static: false })
  referenceMenuPopover: NbPopoverDirective;
  user: SimpleOauth2User; // Logged in user
  userInitials: string;
  userNameHint: string;
  icon: any; // Facets logo
  isDemo = '';
  footerItems: NbMenuItem[] = []; // Menu items for footer
  userName: any;
  showMenuItems: boolean = false;
  private destroy$ = new Subject<void>();
  reInitSidebarMenu: boolean[]; // Signal to be sent to sidebar component
  searchIcon = 'search';
  currentUrl: string;
  hideSidebar: boolean = false;
  localStorageSupported: boolean = false;
  isHovered: boolean = false;
  isHoveredHelp: boolean = false;
  searchDialog: any;
  logo: string;
  showGlobalSpinner: boolean = false;
  facetsAnimatedIcon = './assets/facets-animated.gif';
  clickupTicketUrl: string = '';
  dragging: boolean = false;
  sidebarLogoName = '';
  globalLoaderGifName = '';
  topNavData: any = null;
  referenceQuickLinks: any;
  themeName: string;
  sidebarLogoLink: string;
  enterpriseCustomerName: string = 'facets';
  readonly myAccountQuickLinks = myAccountQuickLinks;
  helpCenterQuickLinks = helpCenterQuickLinks;
  readonly showPaasSaasSwitch = showPaasSaasSwitch;
  docsLink: string = '';

  @ViewChild('logoutTemplate', { static: true })
  private logoutTemplate: TemplateRef<any>;
  @ViewChild('searchTemplate', { static: true })
  private searchTemplate: TemplateRef<any>;
  @ViewChild('inviteUserTemplateRef', { static: true })
  private inviteUserTemplateRef: TemplateRef<any>;

  constructor(
    protected http: HttpClient,
    private cookieService: CookieUtilWrapperService,
    public sidebarService: NbSidebarService,
    private applicationControllerService: ApplicationControllerService,
    private menuService: NbMenuService,
    private dialogBoxService: NbDialogService,
    private router: Router,
    public userAuthoritiesService: UserAuthoritiesService,
    public userService: UserService,
    private sessionContextStoreService: SessionContextStoreService,
    private localStorage: LocalStorageService,
    private redirectOnLoginService: RedirectOnLoginService,
    public featureFlagService: FeatureFlagsService,
    private tableStatefulFiltersService: TableStatefulFiltersService,
    private route: ActivatedRoute,
    public billingService: BillingService,
    private pendo: PendoService,
    private generateTicketUrlService: GenerateTicketURLService,
    private intentDetailsService: IntentDetailsService,
    private contextWidgetStorageService: ContextWidgetStorageService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.redirectOnLoginService.handleRedirectURL(false);
    this.clickupTicketUrl = this.featureFlagService?.getClickupTicketUrl();
    this.localStorageSupported = this.localStorage.isLocalStorageSupported();

    this.initialiseProductTools(this.userService.user);
    this.setUserName(this.userService.user);
    this.cookieService.delete('redirectTo', '/');

    this.getLogo();
    this.isDemo = this.cookieService.get('isDemo');
    this.currentUrl = this.router.url;
    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd || event instanceof NavigationStart,
        ),
      )
      .subscribe((navEvent) => {
        if (navEvent instanceof NavigationStart) {
          this.showGlobalSpinner = true;
        }
        if (navEvent instanceof NavigationEnd) {
          this.showGlobalSpinner = false;
        }
        this.currentUrl = navEvent?.['url'];
      });
    /*
      Adds event listener on document to listen to user button clicks in order to open search popup
    */
    // document.addEventListener('keydown', this.handleKeyDown);
    this.setFeatureFlags();

    this.checkForFooterMenuItemsClick();

    this.intentDetailsService.setAllIntentData();
  }

  setFeatureFlags() {
    this.docsLink =
      this.featureFlagService?.getDocsLink() ||
      'https://readme.facets.cloud/docs/what-is-facets-1';
    this.sidebarLogoName = this.featureFlagService.getSidebarLogoName();
    const globalLoader = this.featureFlagService.getGlobalLoaderGifName();
    if (globalLoader) {
      this.globalLoaderGifName = `./assets/${globalLoader}.gif`;
    }
    this.themeName = this.featureFlagService?.getThemeName();
    this.sidebarLogoLink = this.featureFlagService?.getEnterpriseLogo();
    this.enterpriseCustomerName =
      this.featureFlagService?.getEnterpriseCustomerName();
    const docsLinkObj = this.helpCenterQuickLinks.find(
      (link) => link?.id === 'DOCS',
    );
    if (docsLinkObj) {
      docsLinkObj.url = this.docsLink;
    }
    if (this.enterpriseCustomerName === 'mercedes') {
      this.helpCenterQuickLinks = this.helpCenterQuickLinks.filter(
        (linkItem) => linkItem?.id !== 'COMMUNITY',
      );
    }
    this.setReferenceLinks();
    this.setFooterMenuItems();
  }

  handleAppNavigation(url: string) {
    this.router.navigateByUrl(url);
  }

  setReferenceLinks() {
    let customSidebarItems = [];
    try {
      customSidebarItems = JSON.parse(
        this.featureFlagService?.getSidebarItems(),
      );
      customSidebarItems?.forEach((parentItem) => {
        parentItem?.children?.forEach((child) => {
          (child.target = '_blank'), (child.skipLocationChange = true);
        });
      });
      this.referenceQuickLinks = customSidebarItems?.find(
        (item) => item?.id === 'REFERENCE_LINKS',
      )?.children;
    } catch (error) {}
  }

  /*
    1. Checks if meta(command key is pressed on mac) or control key is pressed on windows
    2. Checks if E key is pressed together with command or control
    3. Opens the popup on combination of above keys
  */
  // handleKeyDown = (event: KeyboardEvent) => {
  //   if ((event?.metaKey || event?.ctrlKey) && event?.key === 'e') {
  //     this.openSearch();
  //   }
  //   if ((event?.metaKey || event?.ctrlKey) && event?.key === '/') {
  //     this.openHelpDashboard();
  //   }
  // };

  initialiseProductTools(user) {
    if (!this.featureFlagService.isMercedesCP) {
      mixpanel.identify(user.userId);
      this.pendo.initialize(user.userId);
      productFruits?.init(PRODUCT_FRUITS_WORKSPACE_CODE, 'en', {
        username: user?.userId,
        props: {
          hostname: window?.location?.hostname,
          isNewUI: true,
        },
      });
    }
  }

  setUserName(user) {
    const USERNAME_LIMIT: number = 18;
    this.userName = user?.attributes['name'] || user?.name;

    this.userInitials =
      this.userName?.length > USERNAME_LIMIT
        ? this.userName.substring(0, USERNAME_LIMIT) + '...'
        : this.userName;

    this.userNameHint =
      this.userName?.length > USERNAME_LIMIT ? this.userName : '';
  }

  // Facets logo for sidebar
  getLogo() {
    this.http
      .get('/public/v1/logo', { responseType: 'text' })
      .subscribe((x) => {
        this.logo = x;
      });
  }

  // Asks for log out confirmation
  openLogoutDialog(): void {
    this.dialogBoxService.open(this.logoutTemplate, {
      hasBackdrop: true,
      autoFocus: false,
    });
  }

  // Logs user out
  logout() {
    const logoutUrl = this.featureFlagService.getLogoutUrl();
    this.cookieService.delete('selection', '/');
    this.tableStatefulFiltersService.flushFilterStates();
    const currentContext = this.cookieService.get('currentContext');
    this.sessionContextStoreService.setSessionContext(
      currentContext,
      this.userName,
      'false',
    );
    this.redirectOnLoginService.preserveRedirectURL();
    this.contextWidgetStorageService.clearContextWidgetStorage();
    this.http.get(`/perform_logout`).subscribe(
      (x: SimpleOauth2User) => {
        triggerAnalyticsEvent(`USER_LOGOUT_SUCCESSFUL`);
      },
      (error) => {
        triggerAnalyticsEvent(`USER_LOGOUT_ERROR`);
        this.resetProductTools();

        if (logoutUrl) {
          this.router.navigate(['/sso-logout']);
        } else {
          this.router.navigate(['/login']);
        }
      },
    );
    //clear local storage after logout
    this.localStorage?.removeFacetsStore();
    try {
      localStorage?.removeItem('logoutUrl');
    } catch (err) {}
  }

  onClickHandleCancel(ref: any) {
    triggerAnalyticsEvent(`USER_LOGOUT_CANCEL`);
    ref.close();
  }

  resetProductTools() {
    mixpanel.reset();
    window?.['productFruits']?.services?.destroy();
  }

  // Footer items for the sidebar
  setFooterMenuItems() {
    this.footerItems = [];
    if (this.featureFlagService?.isSaaSCP) {
      this.footerItems.push({
        title: 'Billing',
        icon: { icon: 'dollar', pack: 'fci' },
        link: '/billing',
      });
    }
    if (!this.featureFlagService?.isMercedesCP) {
      this.footerItems.push({
        title: 'Docs',
        icon: { icon: 'book-open-outline', pack: 'fci' },
        url: this.docsLink,
        target: '_blank',
        skipLocationChange: true,
      });
    }
  }

  // DOM manipulation because nb-menu doesn't have APIs to update the state for active menu items
  // Goes through the anchor tabs of the other menu and updates their classList
  removeAllOtherActiveTabs(currentlyActive: string) {
    let allMenus: any = document.querySelectorAll('nb-menu');
    let allAnchorTags = [
      ...allMenus[0]?.getElementsByTagName('a'),
      ...allMenus[1]?.getElementsByTagName('a'),
    ];
    for (let i = 0; i < allAnchorTags?.length; i++) {
      let item = allAnchorTags[i];
      if (
        (item?.innerText?.length && item?.innerText == currentlyActive) ||
        item?.innerHTML.includes(currentlyActive)
      ) {
        item?.classList.add('active');
      } else {
        item?.classList.remove('active');
      }
    }
  }

  openSearch() {
    this.searchDialog ? this.searchDialog.close() : null;
    this.searchDialog = this.dialogBoxService.open(this.searchTemplate, {
      hasBackdrop: true,
      autoFocus: false,
    });
    triggerAnalyticsEvent(GLOBAL_SEARCH_ICON_CLICK);
  }

  openHelpDashboard() {
    triggerAnalyticsEvent(RAISE_A_TICKET_CLICK);
    if (this.clickupTicketUrl?.length) {
      window.open(
        `${this.clickupTicketUrl}?URL=${window.location.href}&Reported%20by=${this.userService?.user?.name}`,
        '_blank',
      );
    }
  }

  onDragDropClick(event) {
    this.dragging = event;
  }

  onTopNavDataLoad(data) {
    this.topNavData = data;
  }

  @ViewChild('myAccountMenuPopover', { static: false })
  myAccountMenuPopover: NbPopoverDirective;

  @ViewChild('helpCenterMenuPopover', { static: false })
  helpCenterMenuPopover: NbPopoverDirective;

  checkForFooterMenuItemsClick() {
    this.menuService
      .onItemClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (menuItem) => {
        if (menuItem.tag === 'sidebar-my-account-menu' && menuItem.item) {
          triggerAnalyticsEvent(
            `LEFT_NAV_${menuItem?.item?.title?.toUpperCase()}`,
          );
          if (menuItem.item?.['id'] === 'LOGOUT') {
            this.openLogoutDialog();
          } else if (menuItem?.item?.['id'] === 'PRIVACY_POLICY') {
            window.open('https://www.facets.cloud/privacy-policy', '_blank');
          } else {
            this.removeAllOtherActiveTabs('--remove--all--active--');
          }
          this.myAccountMenuPopover?.hide();
        } else if (
          menuItem.tag === 'sidebar-help-center-menu' &&
          menuItem.item
        ) {
          if (menuItem?.item?.['id'] === 'RAISE_TICKET') {
            this.generateTicketUrlService.handleRaiseATicketAction();
          }
        } else if (menuItem.tag === 'footerMenu') {
          this.removeAllOtherActiveTabs(menuItem?.item?.title);
          this.reInitSidebarMenu = [...[true]];
          setTimeout(() => {
            this.reInitSidebarMenu = [false];
          }, 10);
        }
      });
  }

  hidePopover() {
    this.myAccountMenuPopover?.hide();
  }
  onClickHandleLogo(event) {
    triggerAnalyticsEvent(`${event?.toUpperCase()}_LOGO_PROJECTS_REDIRECT`);
  }

  onClickRaiseTicket() {
    this.generateTicketUrlService.handleRaiseATicketAction();
  }

  onClickHelp() {
    triggerAnalyticsEvent('LEFT_NAV_HELP');
  }

  openInviteUser() {
    triggerAnalyticsEvent('LEFT_NAV_INVITE_USER');
    this.dialogBoxService.open(this.inviteUserTemplateRef);
  }

  ngOnDestroy() {
    this.userService.resetUserData();
    this.userAuthoritiesService.resetData();
    this.intentDetailsService?.resetData();
  }
}
